import { supabase } from './supabase'
import type { IdentiteJuridique } from './designationPartie'
import {
  cheminPiece,
  estStatut,
  fichierRecevable,
  lireCapital,
  traduireErreur,
  type KindPiece,
  type OffreChoisie,
  type PlanPme,
  type SignatureLigne,
  type StatutJuridique,
  type StatutOnboarding,
} from './onboardingRegles'

/**
 * Onboarding des clients PME — lectures et écritures Supabase (migration 0034).
 *
 * Aucune règle métier n'est rejouée ici : transitions, pièces, offre, contrat, signature sont décidées par
 * la base ; chaque erreur est traduite en phrase AVANT de remonter à l'écran (`onboardingRegles.ts`).
 */

/** Lève une `Error` à message lisible à partir d'une erreur PostgREST / Storage. */
function lever(error: { message?: string } | null): void {
  if (error) throw new Error(traduireErreur(error.message))
}

/** Une mise à jour qui ne touche aucune ligne = droit manquant (RLS) ou ligne absente : on le dit. */
function exigerLigne(data: unknown[] | null, quoi: string): void {
  if (!data || data.length === 0) throw new Error(`${quoi} : aucune ligne modifiée (droit manquant ou dossier introuvable).`)
}

/* ------------------------------------------------------------------ lecture */

export interface CreneauFiche { weekday: number; start_time: string; end_time: string }

export interface DossierPme extends IdentiteJuridique {
  id: string
  name: string
  contact_name: string | null
  phone: string | null
  email: string | null
  quartier: string | null
  plan_code: string | null
  offslot_surcharge_fcfa: number
  option_longue_distance: boolean
  default_pickup_address: string | null
  default_pickup_lat: number | null
  default_pickup_lng: number | null
  rccm: string | null
  nif: string | null
  /** Ancien nom de `representant_nom` (0035) : jamais lu par du code neuf. */
  gerant_nom: string | null
  siege_adresse: string | null
  offre_choisie: unknown
  onboarding_status: StatutOnboarding
  onboarding_motif: string | null
  onboarding_updated_at: string | null
  created_at: string
  slots: CreneauFiche[]
}

const COLONNES_DOSSIER =
  'id,name,contact_name,phone,email,quartier,plan_code,offslot_surcharge_fcfa,option_longue_distance,default_pickup_address,default_pickup_lat,default_pickup_lng,rccm,nif,gerant_nom,siege_adresse,offre_choisie,onboarding_status,onboarding_motif,onboarding_updated_at,created_at,' +
  'statut_juridique,forme_juridique,forme_juridique_precision,raison_sociale,nom_commercial,capital_fcfa,numero_declaration_entreprenant,representant_nom,representant_qualite,representant_qualite_precision,personne_nom,personne_prenoms,date_naissance,lieu_naissance,type_piece,type_piece_precision,numero_piece,adresse_personne'

/** Toutes les PME de l'organisation opératrice, avec leurs créneaux (pour préremplir l'offre). */
export async function chargerDossiersPme(orgId: string): Promise<DossierPme[]> {
  const [cl, sl] = await Promise.all([
    supabase.from('client_accounts').select(COLONNES_DOSSIER).eq('org_id', orgId).eq('type', 'pme').order('created_at', { ascending: false }),
    supabase.from('client_time_slots').select('client_account_id,weekday,start_time,end_time').eq('org_id', orgId),
  ])
  lever(cl.error)
  lever(sl.error)
  const parClient = new Map<string, CreneauFiche[]>()
  for (const s of (sl.data ?? []) as Array<CreneauFiche & { client_account_id: string }>) {
    const l = parClient.get(s.client_account_id) ?? []
    l.push({ weekday: s.weekday, start_time: s.start_time, end_time: s.end_time })
    parClient.set(s.client_account_id, l)
  }
  return ((cl.data ?? []) as unknown as Array<Omit<DossierPme, 'slots' | 'onboarding_status'> & { onboarding_status: string }>).map((c) => ({
    ...c,
    onboarding_status: estStatut(c.onboarding_status) ? c.onboarding_status : 'prospect',
    slots: parClient.get(c.id) ?? [],
  }))
}

/** Statut de chaque PME — pour le badge de l'écran « Clients ». Illisible = carte vide : jamais bloquant. */
export async function statutsOnboarding(orgId: string): Promise<Map<string, StatutOnboarding>> {
  const { data, error } = await supabase.from('client_accounts').select('id,onboarding_status').eq('org_id', orgId).eq('type', 'pme')
  const m = new Map<string, StatutOnboarding>()
  if (error) return m
  for (const r of (data ?? []) as Array<{ id: string; onboarding_status: string }>) {
    if (estStatut(r.onboarding_status)) m.set(r.id, r.onboarding_status)
  }
  return m
}

/** Catalogue des forfaits PME (`managed_plans`, lecture publique). */
export async function chargerPlansPme(): Promise<PlanPme[]> {
  const { data, error } = await supabase
    .from('managed_plans')
    .select('code,name,price_fcfa,monthly_courses,sort_order')
    .eq('audience', 'pme')
    .order('sort_order')
  lever(error)
  return ((data ?? []) as PlanPme[]).map((p) => ({ code: p.code, name: p.name, price_fcfa: p.price_fcfa, monthly_courses: p.monthly_courses }))
}

export interface PieceDossier {
  id: string
  kind: KindPiece
  file_path: string
  issued_at: string | null
  is_verified: boolean
  verified_at: string | null
  rejected_reason: string | null
  uploaded_at: string
}
export interface SignatureDossier extends SignatureLigne {
  note_manuelle: string | null
  pdf_path: string | null
  destinataire_email: string | null
}
export interface EvenementJournal {
  id: number
  type: 'statut' | 'piece' | 'contrat' | 'acceptation_manuelle' | 'offre'
  de: string | null
  vers: string | null
  detail: string | null
  motif: string | null
  at: string
  auteur: string | null
}

export interface DetailDossier {
  pieces: PieceDossier[]
  signatures: SignatureDossier[]
  journal: EvenementJournal[]
}

export async function chargerDetailDossier(clientId: string): Promise<DetailDossier> {
  const [p, s, j] = await Promise.all([
    supabase.from('client_documents').select('id,kind,file_path,issued_at,is_verified,verified_at,rejected_reason,uploaded_at').eq('client_account_id', clientId).order('uploaded_at', { ascending: false }),
    supabase
      .from('contract_signatures')
      .select('id,method,status,template_version,accepted_at,created_at,lien_signature,note_manuelle,pdf_path,destinataire_email')
      .eq('client_account_id', clientId)
      .order('created_at', { ascending: false }),
    supabase
      .from('onboarding_events')
      .select('id,type,de,vers,detail,motif,at,profiles(full_name)')
      .eq('client_account_id', clientId)
      .order('at', { ascending: false })
      .order('id', { ascending: false })
      .limit(200),
  ])
  lever(p.error)
  lever(s.error)
  lever(j.error)
  return {
    pieces: (p.data ?? []) as PieceDossier[],
    signatures: (s.data ?? []) as SignatureDossier[],
    journal: ((j.data ?? []) as unknown as Array<EvenementJournal & { profiles: { full_name: string | null } | null }>).map((e) => ({
      id: e.id, type: e.type, de: e.de, vers: e.vers, detail: e.detail, motif: e.motif, at: e.at,
      auteur: e.profiles?.full_name ?? null,
    })),
  }
}

/** Le modèle publié courant (le contrat qui serait envoyé) — null si aucun n'est publié. */
export async function contratCourant(): Promise<{ id: string; version: number } | null> {
  const { data, error } = await supabase.rpc('contrat_courant', { p_kind: 'client_pme' })
  if (error) return null
  const l = (Array.isArray(data) ? data[0] : data) as { id: string; version: number } | undefined
  return l?.id ? { id: l.id, version: l.version } : null
}

export async function grilleMensuelle(orgId: string): Promise<{ option_longue_distance_mensuel_fcfa: number } | null> {
  const { data, error } = await supabase.from('tarifs_operes').select('option_longue_distance_mensuel_fcfa').eq('org_id', orgId).maybeSingle()
  if (error || !data) return null
  return { option_longue_distance_mensuel_fcfa: Number((data as { option_longue_distance_mensuel_fcfa: number }).option_longue_distance_mensuel_fcfa) }
}

/* ------------------------------------------------------------------ écritures : fiche */

/** Saisie de l'identité : tout en texte (champs de formulaire) ; vide = NULL en base. */
export interface IdentiteSaisie {
  name: string
  contact_name: string
  phone: string
  email: string
  default_pickup_address: string
  default_pickup_lat: number | null
  default_pickup_lng: number | null
  /** '' = pas encore choisi. */
  statut_juridique: StatutJuridique | ''
  forme_juridique: string
  forme_juridique_precision: string
  raison_sociale: string
  nom_commercial: string
  capital_fcfa: string
  rccm: string
  nif: string
  numero_declaration_entreprenant: string
  siege_adresse: string
  representant_nom: string
  representant_qualite: string
  representant_qualite_precision: string
  personne_nom: string
  personne_prenoms: string
  date_naissance: string
  lieu_naissance: string
  type_piece: string
  type_piece_precision: string
  numero_piece: string
  adresse_personne: string
}

export async function enregistrerIdentite(clientId: string, i: IdentiteSaisie): Promise<void> {
  const v = (s: string) => s.trim() || null
  if (!i.name.trim()) throw new Error('Le nom du client est obligatoire.')
  const capital = lireCapital(i.capital_fcfa)
  /* `gerant_nom` n'est plus écrit : la base le recopie depuis `representant_nom` (0035). */
  const { data, error } = await supabase
    .from('client_accounts')
    .update({
      name: i.name.trim(),
      contact_name: v(i.contact_name),
      phone: v(i.phone),
      email: v(i.email),
      default_pickup_address: v(i.default_pickup_address),
      default_pickup_lat: i.default_pickup_lat,
      default_pickup_lng: i.default_pickup_lng,
      statut_juridique: i.statut_juridique || null,
      forme_juridique: v(i.forme_juridique),
      forme_juridique_precision: v(i.forme_juridique_precision),
      raison_sociale: v(i.raison_sociale),
      nom_commercial: v(i.nom_commercial),
      capital_fcfa: capital,
      rccm: v(i.rccm),
      nif: v(i.nif),
      numero_declaration_entreprenant: v(i.numero_declaration_entreprenant),
      siege_adresse: v(i.siege_adresse),
      representant_nom: v(i.representant_nom),
      representant_qualite: v(i.representant_qualite),
      representant_qualite_precision: v(i.representant_qualite_precision),
      personne_nom: v(i.personne_nom),
      personne_prenoms: v(i.personne_prenoms),
      date_naissance: v(i.date_naissance),
      lieu_naissance: v(i.lieu_naissance),
      type_piece: v(i.type_piece),
      type_piece_precision: v(i.type_piece_precision),
      numero_piece: v(i.numero_piece),
      adresse_personne: v(i.adresse_personne),
    })
    .eq('id', clientId)
    .select('id')
  lever(error)
  exigerLigne(data, 'Enregistrement de l’identité')
}

export async function enregistrerOffre(clientId: string, offre: OffreChoisie): Promise<void> {
  const { data, error } = await supabase.from('client_accounts').update({ offre_choisie: offre }).eq('id', clientId).select('id')
  lever(error)
  exigerLigne(data, 'Enregistrement de l’offre')
}

/** Changement de statut (la base valide la transition ; le motif sert à refuser et suspendre). */
export async function changerStatut(clientId: string, vers: StatutOnboarding, motif?: string): Promise<void> {
  const { data, error } = await supabase
    .from('client_accounts')
    .update({ onboarding_status: vers, ...(motif?.trim() ? { onboarding_motif: motif.trim() } : {}) })
    .eq('id', clientId)
    .select('id')
  lever(error)
  exigerLigne(data, 'Changement de statut')
}

/** RPC propriétaire : contrat signé → actif ; reporte l'offre choisie sur la fiche. */
export async function activerClient(clientId: string): Promise<void> {
  const { error } = await supabase.rpc('activer_client_pme', { p_client: clientId })
  lever(error)
}

/** RPC propriétaire : acceptation manuelle (contrat papier), note obligatoire, scan facultatif. */
export async function accepterManuellement(clientId: string, note: string, cheminScan?: string | null): Promise<void> {
  const { error } = await supabase.rpc('accepter_contrat_manuellement', {
    p_client: clientId,
    p_note: note.trim(),
    p_piece_path: cheminScan ?? null,
  })
  lever(error)
}

/* ------------------------------------------------------------------ écritures : pièces */

/**
 * Dépose une pièce : fichier dans le bucket privé `onboarding`, puis ligne `client_documents`. Si la ligne est
 * refusée, le fichier est retiré (pas d'orphelin). Renvoie le chemin déposé.
 */
export async function deposerPiece(orgId: string, clientId: string, kind: KindPiece, fichier: File, emisLe?: string): Promise<string> {
  const refus = fichierRecevable(fichier)
  if (refus) throw new Error(refus)
  const chemin = cheminPiece(orgId, clientId, kind, fichier.name)
  const up = await supabase.storage.from('onboarding').upload(chemin, fichier, { contentType: fichier.type, upsert: false })
  lever(up.error)
  const { error } = await supabase.from('client_documents').insert({
    org_id: orgId,
    client_account_id: clientId,
    kind,
    file_path: chemin,
    ...(emisLe ? { issued_at: emisLe } : {}),
  })
  if (error) {
    await supabase.storage.from('onboarding').remove([chemin])
    lever(error)
  }
  return chemin
}

/** Dépose seulement le fichier (scan d'un contrat papier) et renvoie son chemin, sans ligne de pièce. */
export async function deposerScan(orgId: string, clientId: string, fichier: File): Promise<string> {
  const refus = fichierRecevable(fichier)
  if (refus) throw new Error(refus)
  const chemin = cheminPiece(orgId, clientId, 'autre', fichier.name)
  const up = await supabase.storage.from('onboarding').upload(chemin, fichier, { contentType: fichier.type, upsert: false })
  lever(up.error)
  return chemin
}

export async function validerPiece(id: string): Promise<void> {
  const { data, error } = await supabase.from('client_documents').update({ is_verified: true }).eq('id', id).select('id')
  lever(error)
  exigerLigne(data, 'Validation de la pièce')
}

export async function rejeterPiece(id: string, motif: string): Promise<void> {
  if (!motif.trim()) throw new Error(traduireErreur('motif_rejet_requis'))
  const { data, error } = await supabase.from('client_documents').update({ rejected_reason: motif.trim() }).eq('id', id).select('id')
  lever(error)
  exigerLigne(data, 'Rejet de la pièce')
}

/** Retire une pièce NON validée (ligne puis fichier). */
export async function retirerPiece(p: Pick<PieceDossier, 'id' | 'file_path'>): Promise<void> {
  const { data, error } = await supabase.from('client_documents').delete().eq('id', p.id).select('id')
  lever(error)
  exigerLigne(data, 'Retrait de la pièce')
  await supabase.storage.from('onboarding').remove([p.file_path])
}

/** Lien signé de 5 minutes vers un fichier du bucket privé (pièce ou contrat signé). */
export async function urlSignee(chemin: string): Promise<string> {
  const { data, error } = await supabase.storage.from('onboarding').createSignedUrl(chemin, 300)
  lever(error)
  if (!data?.signedUrl) throw new Error('Lien indisponible.')
  return data.signedUrl
}

/* ------------------------------------------------------------------ contrat : envoi Documenso */

export interface ReponseEnvoiContrat {
  ok: boolean
  signature_id: string
  document_id: string
  lien_signature: string
  email_envoye: boolean
  telephone: string | null
  signataire_nom: string | null
  whatsapp_url: string | null
  deja_envoye?: boolean
  /** Renvoi par e-mail demandé mais impossible : contrat préparé sans vraie adresse (« adresse_technique »). */
  email_impossible?: string
}

/**
 * Edge function `envoyer-contrat-signature`. Appel direct (et non `appelerFonction`) pour traduire PAR CODE :
 * la fonction répond `{error, detail}` et `detail` vaut parfois un simple statut (« prospect »), inutilisable
 * tel quel à l'écran.
 */
export async function envoyerContrat(clientId: string, envoyerEmail: boolean): Promise<ReponseEnvoiContrat> {
  const { data, error } = await supabase.functions.invoke('envoyer-contrat-signature', {
    body: { client_id: clientId, envoyer_email: envoyerEmail },
  })
  if (error) {
    let code = ''
    let detail = ''
    try {
      const contexte = (error as { context?: { json?: () => Promise<unknown> } }).context
      const j = (await contexte?.json?.()) as { error?: string; detail?: string } | undefined
      code = j?.error ?? ''
      detail = j?.detail ?? ''
    } catch {
      /* corps non JSON : on retombe sur le message brut */
    }
    throw new Error(traduireErreur(code || error.message, detail))
  }
  const r = data as (ReponseEnvoiContrat & { error?: string; detail?: string }) | null
  if (r && r.error) throw new Error(traduireErreur(r.error, r.detail))
  return data as ReponseEnvoiContrat
}

/* ------------------------------------------------------------------ modèles de contrat */

export interface ModeleContrat {
  id: string
  version: number
  titre: string
  corps: string
  provisoire: boolean
  publieLe: string | null
}
export interface ChampsModele { titre: string; corps: string; provisoire: boolean }

interface LigneModele { id: string; version: number; titre: string; corps_md: string; provisoire: boolean; published_at: string | null }
const COLONNES_MODELE = 'id,version,titre,corps_md,provisoire,published_at'
const versModele = (m: LigneModele): ModeleContrat => ({
  id: m.id, version: m.version, titre: m.titre, corps: m.corps_md, provisoire: m.provisoire, publieLe: m.published_at,
})

export async function listerModeles(): Promise<ModeleContrat[]> {
  const { data, error } = await supabase.from('contract_templates').select(COLONNES_MODELE).eq('kind', 'client_pme').order('version', { ascending: false })
  lever(error)
  return ((data as LigneModele[] | null) ?? []).map(versModele)
}

/** Nouveau brouillon = dernière version + 1 (lue à la création), copiée de la dernière pour repartir de son texte. */
export async function creerBrouillon(c: ChampsModele): Promise<ModeleContrat> {
  const { data: dernier, error: e1 } = await supabase
    .from('contract_templates').select('version').eq('kind', 'client_pme').order('version', { ascending: false }).limit(1).maybeSingle()
  lever(e1)
  const version = ((dernier as { version: number } | null)?.version ?? 0) + 1
  const { data, error } = await supabase
    .from('contract_templates')
    .insert({ kind: 'client_pme', version, titre: c.titre.trim(), corps_md: c.corps, provisoire: c.provisoire })
    .select(COLONNES_MODELE)
    .single()
  lever(error)
  return versModele(data as LigneModele)
}

export async function enregistrerBrouillon(id: string, c: ChampsModele): Promise<void> {
  const { data, error } = await supabase
    .from('contract_templates').update({ titre: c.titre.trim(), corps_md: c.corps, provisoire: c.provisoire }).eq('id', id).select('id')
  lever(error)
  exigerLigne(data, 'Enregistrement du brouillon')
}

/** Publie un brouillon (avec ses dernières modifications, en un seul UPDATE). La base refuse un texte provisoire. */
export async function publierModele(id: string, c: ChampsModele): Promise<void> {
  const { data, error } = await supabase
    .from('contract_templates')
    .update({ titre: c.titre.trim(), corps_md: c.corps, provisoire: c.provisoire, published_at: new Date().toISOString() })
    .eq('id', id)
    .select('id')
  lever(error)
  exigerLigne(data, 'Publication du contrat')
}
